/** Detect and normalize social / video URLs for CMS embeds. */

export type EmbedProvider = 'youtube' | 'vimeo' | 'x' | 'instagram' | 'tiktok';

export interface ParsedEmbed {
  provider: EmbedProvider;
  /** Safe https iframe src (or provider embed URL). */
  src: string;
  /** Original pasted URL for reference. */
  originalUrl: string;
}

const YT =
  /(?:youtube\.com\/(?:watch\?(?:[^&\s]*&)*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;
const VIMEO = /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/i;
const X = /(?:twitter\.com|x\.com)\/(?:#!\/)?\w+\/status(?:es)?\/(\d+)/i;
const IG = /instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/i;
const TIKTOK = /tiktok\.com\/(?:@[\w.-]+\/video\/|embed\/v2\/)?(\d+)/i;

export const EMBED_HOST_ALLOWLIST = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'www.instagram.com',
  'instagram.com',
  'www.tiktok.com',
  'platform.twitter.com',
]);

export function parseEmbedUrl(raw: string): ParsedEmbed | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let url: URL;
  try {
    url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const href = url.href;

  const yt = href.match(YT);
  if (yt?.[1]) {
    return {
      provider: 'youtube',
      src: `https://www.youtube.com/embed/${yt[1]}`,
      originalUrl: href,
    };
  }

  const vimeo = href.match(VIMEO);
  if (vimeo?.[1]) {
    return {
      provider: 'vimeo',
      src: `https://player.vimeo.com/video/${vimeo[1]}`,
      originalUrl: href,
    };
  }

  const x = href.match(X);
  if (x?.[1]) {
    return {
      provider: 'x',
      src: `https://platform.twitter.com/embed/Tweet.html?id=${x[1]}`,
      originalUrl: href,
    };
  }

  const ig = href.match(IG);
  if (ig?.[1]) {
    return {
      provider: 'instagram',
      src: `https://www.instagram.com/p/${ig[1]}/embed`,
      originalUrl: href,
    };
  }

  const tt = href.match(TIKTOK);
  if (tt?.[1]) {
    return {
      provider: 'tiktok',
      src: `https://www.tiktok.com/embed/v2/${tt[1]}`,
      originalUrl: href,
    };
  }

  return null;
}

export function isAllowedEmbedSrc(src: string): boolean {
  try {
    const u = new URL(src);
    return u.protocol === 'https:' && EMBED_HOST_ALLOWLIST.has(u.hostname);
  } catch {
    return false;
  }
}
